import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Search, HardHat, RotateCcw, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const emptyEntry = { mansione_label: '', keywords: '', dpi: '', order: 100 };

function entryToForm(e) {
  return {
    id: e?.id || '',
    mansione_label: e?.mansione_label || '',
    keywords: (e?.keywords || []).join(', '),
    dpi: (e?.dpi || []).join('\n'),
    order: e?.order ?? 100,
  };
}

function formToPayload(f) {
  return {
    mansione_label: (f.mansione_label || '').trim(),
    keywords: (f.keywords || '').split(',').map((s) => s.trim()).filter(Boolean),
    dpi: (f.dpi || '').split('\n').map((s) => s.trim()).filter(Boolean),
    order: Number(f.order) || 100,
  };
}

export default function DpiCatalogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEntry);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/dpi-catalog');
      setItems(r.data || []);
    } catch {
      toast.error('Errore caricamento catalogo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.mansione_label || '').toLowerCase().includes(q) ||
      (it.keywords || []).some((k) => k.toLowerCase().includes(q)) ||
      (it.dpi || []).some((d) => d.toLowerCase().includes(q)),
    );
  }, [items, search]);

  const openNew = () => {
    setEditing(null);
    setForm(entryToForm({ mansione_label: '', keywords: [], dpi: [], order: (items.length + 1) * 10 }));
    setDialogOpen(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setForm(entryToForm(entry));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = formToPayload(form);
    if (!payload.mansione_label) { toast.error('Etichetta mansione obbligatoria'); return; }
    if (payload.keywords.length === 0) { toast.error('Inserisci almeno una keyword'); return; }
    if (payload.dpi.length === 0) { toast.error('Inserisci almeno un DPI'); return; }
    try {
      if (editing?.id) {
        await api.put(`/dpi-catalog/${editing.id}`, payload);
        toast.success('Voce aggiornata');
      } else {
        await api.post('/dpi-catalog', payload);
        toast.success('Voce creata');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await api.delete(`/dpi-catalog/${deleteTarget.id}`);
      toast.success('Voce eliminata');
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Vuoi davvero RESETTARE il catalogo ai valori standard? Tutte le tue modifiche andranno perse.')) return;
    setResetting(true);
    try {
      const r = await api.post('/dpi-catalog/reset');
      toast.success(`Catalogo reimpostato: ${r.data.count} voci`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore reset (solo super_admin/admin)');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dpi-catalog-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <HardHat className="w-7 h-7 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">Catalogo DPI per Mansione</h1>
            <Badge variant="secondary" data-testid="dpi-catalog-count">{items.length} mansioni</Badge>
          </div>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Mappa <strong>mansione → DPI standard</strong> usata per popolare automaticamente la tabella
            del template <strong>Art. 18 DPI</strong>. Le <em>keyword</em> servono al matching: quando un
            dipendente ha mansione "Aiuto cuoco", il sistema cerca una voce le cui keyword (es.{' '}
            <code>aiuto cuoco</code>) compaiono nel testo della mansione.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={resetting}
            data-testid="dpi-catalog-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Ripristina default
          </Button>
          <Button onClick={openNew} data-testid="dpi-catalog-new-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuova mansione
          </Button>
        </div>
      </header>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per mansione, keyword o DPI..."
          className="pl-9"
          data-testid="dpi-catalog-search"
        />
      </div>

      {loading && (
        <Card><CardContent className="py-10 text-center text-muted-foreground">Caricamento...</CardContent></Card>
      )}

      {!loading && filtered.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            Nessuna voce trovata. Clicca <strong>Nuova mansione</strong> per aggiungerne una.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((item) => (
          <Card key={item.id} className="overflow-hidden" data-testid={`dpi-catalog-item-${item.id}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <span className="truncate">{item.mansione_label}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => openEdit(item)}
                    data-testid={`dpi-catalog-edit-${item.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => setDeleteTarget(item)}
                    data-testid={`dpi-catalog-delete-${item.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">Keyword di matching</div>
                <div className="flex flex-wrap gap-1">
                  {(item.keywords || []).map((k) => (
                    <Badge key={k} variant="outline" className="font-mono text-[10px]">{k}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">DPI ({item.dpi?.length || 0})</div>
                <ul className="text-xs space-y-0.5">
                  {(item.dpi || []).map((d) => (
                    <li key={d} className="flex gap-1.5"><span>•</span><span>{d}</span></li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog crea/modifica */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica mansione' : 'Nuova mansione'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Etichetta visualizzata *</Label>
              <Input
                value={form.mansione_label}
                onChange={(e) => setForm({ ...form, mansione_label: e.target.value })}
                placeholder="Es. Pizzaiolo / Aiuto cuoco"
                data-testid="dpi-form-label"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Keyword di matching (separate da virgola) *</Label>
              <Input
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="pizzaiolo, aiuto cuoco, chef"
                data-testid="dpi-form-keywords"
              />
              <p className="text-[10px] text-muted-foreground">
                Tutto in lowercase. Il sistema cerca se UNA di queste compare nella mansione del dipendente.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>DPI standard (UNO per riga) *</Label>
              <textarea
                value={form.dpi}
                onChange={(e) => setForm({ ...form, dpi: e.target.value })}
                placeholder={'Scarpe antinfortunistiche antiscivolo\nGrembiule/Camice\nGuanti di protezione\nCuffia/Copricapo\nMascherina FFP2/FFP3'}
                className="w-full min-h-[140px] px-3 py-2 text-sm border rounded-md font-mono"
                data-testid="dpi-form-dpi"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ordine</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                data-testid="dpi-form-order"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" /> Annulla
            </Button>
            <Button onClick={handleSave} data-testid="dpi-form-save">
              <Save className="w-4 h-4 mr-2" /> {editing ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{deleteTarget?.mansione_label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione è irreversibile. Per i dipendenti con questa mansione i DPI torneranno ai DPI generici di default.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
